import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Activity, AlertTriangle, Target } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import logger from '../utils/logger';


const IPM_COLORS = { ALTO: '#22c55e', MODERADO: '#eab308', ELEVADO: '#f97316', CRITICO: '#ef4444' };

const IPMGaugeMini = ({ score, level }) => {
  const r = 45, s = 8;
  const circ = Math.PI * r;
  const off = circ * (1 - Math.min((score || 0) / 100, 1));
  const color = IPM_COLORS[level] || '#94a3b8';
  return (
    <svg width={(r + s) * 2} height={r + s + 8} viewBox={`0 0 ${(r + s) * 2} ${r + s + 8}`}>
      <path d={`M ${s} ${r + s} A ${r} ${r} 0 0 1 ${r * 2 + s} ${r + s}`} fill="none" stroke="#e5e7eb" strokeWidth={s} strokeLinecap="round" />
      <path d={`M ${s} ${r + s} A ${r} ${r} 0 0 1 ${r * 2 + s} ${r + s}`} fill="none" stroke={color} strokeWidth={s} strokeLinecap="round"
        strokeDasharray={circ} strokeDashoffset={off} style={{ transition: 'stroke-dashoffset 1s ease-in-out' }} />
      <text x={r + s} y={r - 2} textAnchor="middle" className="text-xl font-bold" fill={color}>{score?.toFixed(1) ?? '—'}</text>
      <text x={r + s} y={r + 15} textAnchor="middle" className="text-[10px]" fill="#9ca3af">IPM</text>
    </svg>
  );
};

const MiPulsoSection = () => {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const t = {
    es: { title: 'Mi Pulso', noData: 'Responde una encuesta de pulso para ver tus resultados.', gaps: 'Mayor Brecha', best: 'Mejor Activado', completed: 'Períodos completados' },
    en: { title: 'My Pulse', noData: 'Complete a pulse survey to see your results.', gaps: 'Top Gap', best: 'Best Activated', completed: 'Completed periods' }
  }[language] || {};

  useEffect(() => {
    loadData();
  }, [language]);

  const loadData = async () => {
    try {
      const res = await axios.get(`/v5/pulso/employee/mi-pulso?lang=${language}`);
      if (res.data.success) setData(res.data.data);
    } catch (e) {
      logger.error('Mi Pulso error:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (!data?.has_data) return (
    <Card className="mb-6" data-testid="mi-pulso-section">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg text-teal-700">
          <Activity className="w-5 h-5" /> {t.title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-center py-6 text-gray-400">
          <Activity className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">{t.noData}</p>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Card className="mb-6" data-testid="mi-pulso-section">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg text-teal-700">
          <Activity className="w-5 h-5" /> {t.title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row items-center gap-6">
          {/* IPM Gauge */}
          <div className="flex flex-col items-center">
            <IPMGaugeMini score={data.ipm} level={data.ipm_level} />
            <Badge style={{ backgroundColor: IPM_COLORS[data.ipm_level], color: '#fff' }} className="text-xs mt-1">
              {data.ipm_level}
            </Badge>
            {data.ipm_interpretation && (
              <p className="text-xs text-gray-500 mt-1 text-center max-w-[180px]">{data.ipm_interpretation}</p>
            )}
          </div>

          {/* Top gaps & best */}
          <div className="flex-1 grid grid-cols-2 gap-4 w-full">
            <div>
              <p className="text-xs font-semibold text-red-600 mb-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> {t.gaps}
              </p>
              {data.top_gaps?.slice(0, 2).map((g, i) => (
                <div key={i} className="flex justify-between items-center py-0.5 text-xs">
                  <span className="text-gray-600 truncate mr-1">{g.label}</span>
                  <span className="text-red-500 font-medium">{(g.iad ?? g.iac)?.toFixed?.(0) ?? g.iad ?? g.iac}</span>
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs font-semibold text-green-600 mb-1 flex items-center gap-1">
                <Target className="w-3 h-3" /> {t.best}
              </p>
              {data.top_best?.slice(0, 2).map((g, i) => (
                <div key={i} className="flex justify-between items-center py-0.5 text-xs">
                  <span className="text-gray-600 truncate mr-1">{g.label}</span>
                  <span className="text-green-500 font-medium">{(g.iad ?? g.iac)?.toFixed?.(0) ?? g.iad ?? g.iac}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Dimensions mini bars */}
          <div className="flex-1 space-y-1 w-full">
            {data.dimensions?.map(c => (
              <div key={c.key} className="flex items-center gap-2 text-xs">
                <span className="w-24 text-gray-500 truncate">{c.label}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                  <div className="h-1.5 rounded-full" style={{ width: `${Math.min(c.iad ?? c.iac, 100)}%`, backgroundColor: IPM_COLORS[c.level] || '#94a3b8' }} />
                </div>
                <span className="w-8 text-right text-gray-500">{(c.iad ?? c.iac)?.toFixed?.(0) ?? c.iad ?? c.iac}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MiPulsoSection;
